function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

class FacetsToggle {
  constructor() {
    this.container = document.getElementById('Facet-Drawer');
    const button = document.getElementById('Facets-Toggle');
    if (button && this.container) {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        document.body.classList.toggle('open-cc');
        this.container.classList.toggle('active');
      });
    }
  }
}

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    this.debouncedOnSubmit = debounce((event) => this.onSubmitHandler(event), 350);
    const formEl = this.querySelector('form');
    if (formEl) formEl.addEventListener('input', this.debouncedOnSubmit);
  }

  static setListeners() {
    window.addEventListener('popstate', (event) => {
      const searchParams = event.state ? event.state.searchParams : window.location.search.slice(1);
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    });

    const onResize = debounce(() => {
      const root = document.documentElement;
      const header = document.querySelector('.header');
      if (header && window.innerWidth <= 768) root.style.setProperty('--header-height-mobile', header.clientHeight + 'px');
    }, 200);
    window.addEventListener('resize', onResize);
    onResize();
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => element.classList.toggle('disabled', disable));
  }

  static serializeForm(form) {
    const searchParams = new URLSearchParams();

    form.querySelectorAll('input, select, textarea').forEach((field) => {
      if (!field.name || field.disabled) return;

      if ((field.type === 'checkbox' || field.type === 'radio') && !field.checked) return;
      if (!FacetFiltersForm.shouldSerializePriceField(field)) return;

      if (field.tagName === 'SELECT' && field.multiple) {
        Array.from(field.selectedOptions).forEach((option) => {
          searchParams.append(field.name, option.value);
        });
        return;
      }

      searchParams.append(field.name, field.value || '');
    });

    return searchParams;
  }

  static shouldSerializePriceField(field) {
    if (field.name !== 'filter.v.price.gte' && field.name !== 'filter.v.price.lte') return true;

    const wrapper = field.closest('price-slider');
    const slider = wrapper ? wrapper.querySelector('.price_slider') : null;
    if (!wrapper || !slider) return false;

    const userTouchedPrice = wrapper.dataset.priceTouched === 'true';
    const hasActiveMin = slider.dataset.minValue !== undefined && slider.dataset.minValue !== '';
    const hasActiveMax = slider.dataset.maxValue !== undefined && slider.dataset.maxValue !== '';

    if (userTouchedPrice) return true;
    if (field.name === 'filter.v.price.gte') return hasActiveMin;
    if (field.name === 'filter.v.price.lte') return hasActiveMax;

    return false;
  }

  static preserveSearchQuery(searchParams) {
    const currentParams = new URLSearchParams(window.location.search);
    const currentQuery = currentParams.get('q');
    const formQuery = searchParams.get('q');

    if (currentQuery && (!formQuery || formQuery.trim() === '')) {
      searchParams.set('q', currentQuery);
    }

    if (searchParams.get('q') && !searchParams.get('options[prefix]')) {
      searchParams.set('options[prefix]', currentParams.get('options[prefix]') || 'last');
    }

    return searchParams;
  }

  static pruneUnavailableFacetValues(root = document) {
    root.querySelectorAll('.facets__form li, .facets__mobile_form li').forEach((item) => {
      const input = item.querySelector('input[type="checkbox"]');
      const countText = item.querySelector('.count')?.textContent.trim();
      if (input && !input.checked && (input.disabled || countText === '(0)' || countText === '0')) item.remove();
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const grid = document.getElementById('ProductGridContainer');
    if (!grid) return;
    const collection = grid.querySelector('.collection');
    if (collection) collection.classList.add('loading');
    document.querySelectorAll('.thb-filter-count').forEach((item) => item.classList.add('loading'));

    FacetFiltersForm.getSections().forEach((section) => {
      if (!section.section) return;
      const url = `${window.location.pathname}?section_id=${section.section}${searchParams ? '&' + searchParams : ''}`;
      const filterDataUrl = (element) => element.url === url;
      if (FacetFiltersForm.filterData.some(filterDataUrl)) FacetFiltersForm.renderSectionFromCache(filterDataUrl, event);
      else FacetFiltersForm.renderSectionFromFetch(url, event);
    });
    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error('Facet section request failed: ' + response.status);
        return response.text();
      })
      .then((html) => {
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }].slice(-20);
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
      })
      .catch(() => {
        window.location.href = window.location.pathname + window.location.search;
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const cached = FacetFiltersForm.filterData.find(filterDataUrl);
    if (!cached) return;
    FacetFiltersForm.renderFilters(cached.html, event);
    FacetFiltersForm.renderProductGridContainer(cached.html);
    FacetFiltersForm.renderProductCount(cached.html);
  }

  static renderProductGridContainer(html) {
    const current = document.getElementById('ProductGridContainer');
    const incoming = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductGridContainer');
    if (current && incoming) current.innerHTML = incoming.innerHTML;
  }

  static renderProductCount(html) {
    const incoming = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount');
    if (!incoming) return;
    document.querySelectorAll('.thb-filter-count').forEach((item) => {
      item.innerHTML = incoming.innerHTML;
      item.classList.remove('loading');
    });
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const incomingFilters = parsedHTML.querySelectorAll('#FacetFiltersForm .js-filter');
    const activeIndex = event && event.target ? event.target.closest('.js-filter')?.dataset.index : null;

    incomingFilters.forEach((element) => {
      if (activeIndex && element.dataset.index === activeIndex) return;
      const index = element.dataset.index || '';
      const target = document.querySelector(`.js-filter[data-index="${CSS.escape(index)}"]`);
      if (target) target.innerHTML = element.innerHTML;
    });

    if (activeIndex) {
      const source = Array.from(incomingFilters).find((el) => el.dataset.index === activeIndex);
      const target = event.target.closest('.js-filter');
      if (source && target) FacetFiltersForm.renderCounts(source, target);
    }

    const incomingMobile = parsedHTML.getElementById('FacetFiltersFormMobile');
    const currentMobile = document.getElementById('FacetFiltersFormMobile');
    if (incomingMobile && currentMobile) currentMobile.innerHTML = incomingMobile.innerHTML;

    FacetFiltersForm.pruneUnavailableFacetValues();
    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);
  }

  static renderActiveFacets(html) {
    const source = html.querySelector('.active-facets');
    if (!source) return;
    document.querySelectorAll('.active-facets').forEach((target) => { target.innerHTML = source.innerHTML; });
    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const source = html.querySelector('.mobile-filter-count');
    if (!source) return;
    document.querySelectorAll('.mobile-filter-count').forEach((target) => { target.innerHTML = source.innerHTML; });
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');
    if (sourceElement && targetElement) targetElement.outerHTML = sourceElement.outerHTML;
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams ? '?' + searchParams : ''}`);
  }

  static getSections() {
    const grid = document.getElementById('product-grid');
    return [{ section: grid ? grid.dataset.id : null }];
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const form = event.target.closest('form');
    if (!form) return;
    const searchParams = FacetFiltersForm.preserveSearchQuery(FacetFiltersForm.serializeForm(form));
    if (searchParams.get('filter.v.price.gte') === '0.00') searchParams.delete('filter.v.price.gte');
    const priceSlider = document.querySelector('.price_slider');
    if (priceSlider && searchParams.get('filter.v.price.lte')) {
      const max = parseFloat(priceSlider.dataset.max);
      const selected = parseFloat(searchParams.get('filter.v.price.lte'));
      if (Number.isFinite(max) && Number.isFinite(selected) && selected === max) searchParams.delete('filter.v.price.lte');
    }
    searchParams.delete('page');
    FacetFiltersForm.renderPage(searchParams.toString(), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const href = event.currentTarget.href;
    const params = new URLSearchParams(href.indexOf('?') === -1 ? '' : href.slice(href.indexOf('?') + 1));
    FacetFiltersForm.preserveSearchQuery(params);
    FacetFiltersForm.renderPage(params.toString());
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
if (!customElements.get('facet-filters-form')) customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.pruneUnavailableFacetValues();
FacetFiltersForm.setListeners();

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('a').forEach((item) => {
      item.addEventListener('click', (event) => {
        event.preventDefault();
        const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
        if (form && typeof form.onActiveFilterClick === 'function') form.onActiveFilterClick(event);
      });
    });
  }
}
if (!customElements.get('facet-remove')) customElements.define('facet-remove', FacetRemove);

class PriceSlider extends HTMLElement {
  connectedCallback() {
    const slider = this.querySelector('.price_slider');
    const amounts = this.querySelector('.price_slider_amount, .sl-price-row');
    if (!slider || !amounts || typeof noUiSlider === 'undefined') return;
    const max = parseFloat(slider.dataset.max);
    if (!Number.isFinite(max) || max <= 0) return;
    if (slider.classList.contains('noUi-target') && slider.noUiSlider) slider.noUiSlider.destroy();
    noUiSlider.create(slider, {
      start: [parseFloat(slider.dataset.minValue || 0), parseFloat(slider.dataset.maxValue || max)],
      connect: true,
      step: 10,
      direction: document.dir,
      handleAttributes: [{ 'aria-label': 'lower' }, { 'aria-label': 'upper' }],
      range: { min: 0, max }
    });
    slider.noUiSlider.on('update', function (values) {
      const minInput = amounts.querySelector('.field__input_min');
      const maxInput = amounts.querySelector('.field__input_max');
      if (minInput) minInput.value = values[0];
      if (maxInput) maxInput.value = values[1];
    });
    amounts.querySelectorAll('.field__input_min, .field__input_max').forEach((input) => {
      input.addEventListener('input', () => { this.dataset.priceTouched = 'true'; });
      input.addEventListener('change', () => { this.dataset.priceTouched = 'true'; });
    });
    slider.noUiSlider.on('change', () => {
      this.dataset.priceTouched = 'true';
      const form = this.closest('facet-filters-form');
      if (form) form.querySelector('form')?.dispatchEvent(new Event('input', { bubbles: true }));
    });
  }
}
if (!customElements.get('price-slider')) customElements.define('price-slider', PriceSlider);
window.addEventListener('load', () => new FacetsToggle());
